import type { TOC } from '@ember/component/template-only';

/**
 * Padding size options for the card. `xs` and `xl` exist because the surfaces
 * this component is meant to replace use them: a metric tile is p-3, a
 * full-width empty state is p-8. Without them those surfaces stay hand-rolled.
 */
export type CardPadding = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Radius steps a card can take, from DESIGN.md's radius table. `nested` is the
 * 12px step used by a card inside another card — a metric tile in a panel, a
 * sub-panel in a hero — where the outer surface keeps the 16px `card` step so
 * the nesting reads as hierarchy.
 */
export type CardRadius = 'compact' | 'nested' | 'card';

/**
 * How far the card sits off the surface behind it. `none` is the flat variant —
 * a hairline and no shadow — which is what one of many peers inside an already
 * elevated panel takes, so the panel is the only thing that reads as lifted
 * (DESIGN.md § Elevation).
 */
export type CardElevation = 'none' | 'card' | 'raised';

interface CardSignature {
  Args: {
    /** Padding size (default: 'md' = p-5) */
    padding?: CardPadding;
    /**
     * Radius step (default: 'card' = rounded-2xl, 16px). 'nested' is 12px for a
     * card inside a card; 'compact' is 8px for dense list rows.
     */
    radius?: CardRadius;
    /**
     * Elevation step (default: 'card'). Use 'none' for a row or tile that is one
     * of many peers inside a panel, and 'raised' for a surface deliberately
     * lifted above its neighbours.
     */
    elevation?: CardElevation;
    /**
     * Deprecated alias for `@radius="compact"`. Kept so existing call sites keep
     * rendering; prefer `@radius`, which can also express the nested step.
     */
    compact?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

const paddingClasses: Record<CardPadding, string> = {
  none: 'tio-card--padding-none',
  xs: 'tio-card--padding-xs',
  sm: 'tio-card--padding-sm',
  md: 'tio-card--padding-md',
  lg: 'tio-card--padding-lg',
  xl: 'tio-card--padding-xl',
};

const radiusClasses: Record<CardRadius, string> = {
  compact: 'tio-card--radius-compact',
  nested: 'tio-card--radius-nested',
  card: 'tio-card--radius-card',
};

const elevationClasses: Record<CardElevation, string> = {
  none: 'tio-card--elevation-none',
  card: 'tio-card--elevation-card',
  raised: 'tio-card--elevation-raised',
};

const getPadding = (padding: CardPadding = 'md') => paddingClasses[padding];

const getElevation = (elevation: CardElevation = 'card') => elevationClasses[elevation];

// `@compact` predates `@radius` and means the 8px step.
const getRadius = (radius: CardRadius | undefined, compact: boolean | undefined) =>
  radiusClasses[radius ?? (compact ? 'compact' : 'card')];

/**
 * Shared card surface, border, radius, elevation, and padding; see DESIGN.md for the elevation
 * contract.
 */
const Card: TOC<CardSignature> = <template>
  <div
    data-test-card
    class="tio-card
      {{getRadius @radius @compact}}
      {{getElevation @elevation}}
      {{getPadding @padding}}"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

export default Card;

export type { CardSignature };
